import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, ChevronRight, CornerUpLeft, CalendarClock } from "lucide-react";
import { api, errMsg, fmtDate, fmtDay } from "../lib/api";
import { Panel, Empty, btnPrimary } from "./ui-kit";
import { StatusBadge, RoleBadge, TypeBadge, ClassLinkedBadge } from "./Badges";

const Row = ({ t, scope, onPick }) => {
  const nav = useNavigate();
  return (
    <Panel data-testid={`task-row-${t.id}`} className="group flex cursor-pointer flex-col gap-4 p-5 transition-colors duration-200 hover:border-[#FF6B00]/50 hover:bg-[#1C202E] md:flex-row md:items-center" onClick={() => nav(`/tasks/${t.id}`)}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <TypeBadge type={t.content_type} />
          {t.class_linked && <ClassLinkedBadge />}
          {t.sent_back && t.status === "queued" && <span className="inline-flex items-center gap-1 rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 font-mono text-[10px] uppercase text-rose-300"><CornerUpLeft className="h-3 w-3" />Sent back</span>}
        </div>
        <div className="mt-2 truncate font-heading text-lg font-semibold" data-testid={`task-row-title-${t.id}`}>{t.title}</div>
        <div className="mt-1 text-xs text-slate-500">{t.process_name}{t.class_name && ` · ${t.class_name}`}{t.created_by && ` · Assigned by ${t.created_by}`}</div>
        {t.due_date && (
          <div data-testid={`task-row-deadline-${t.id}`} className={`mt-2 inline-flex items-center gap-1.5 text-xs ${t.overdue ? "font-semibold text-rose-300" : "text-slate-300"}`}>
            <CalendarClock className="h-3.5 w-3.5" />{t.overdue ? "Overdue · " : "Deadline "}{fmtDay(t.due_date)}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 md:justify-end">
        {t.status !== "completed" ? (
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase text-slate-500">Step {t.current_step + 1}/{t.total_steps}</div>
            <div className="mt-1 flex items-center gap-2"><span className="text-sm">{t.current_step_name}</span><RoleBadge role={t.current_role} /></div>
          </div>
        ) : <span className="text-xs text-slate-500">{fmtDate(t.updated_at)}</span>}
        <StatusBadge status={t.status} />
        {t.assignee_name && t.status === "in_progress" && <span className="text-xs text-slate-400">@{t.assignee_name}</span>}
        {scope === "queue" ? (
          <button data-testid={`shared-queue-pickup-button-${t.id}`} className={btnPrimary} onClick={(e) => { e.stopPropagation(); onPick(t.id); }}>Pick up</button>
        ) : <ChevronRight className="h-4 w-4 text-slate-600 transition-transform group-hover:translate-x-1" />}
      </div>
    </Panel>
  );
};

export const TaskList = ({ scope, refreshKey, filter = () => true, emptyTitle, emptyDesc }) => {
  const [tasks, setTasks] = useState(null);
  const nav = useNavigate();
  const load = useCallback(() => api.get(`/tasks?scope=${scope}`).then((r) => setTasks(r.data)), [scope]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const pick = async (id) => {
    try {
      await api.post(`/tasks/${id}/pickup`);
      toast.success("Task picked up. It's in My Tasks now.");
      nav(`/tasks/${id}`);
    } catch (e) {
      toast.error(errMsg(e));
      load();
    }
  };
  if (!tasks) return <div className="text-slate-500">Loading…</div>;
  const list = tasks.filter(filter);
  if (!list.length) return <Empty icon={Inbox} title={emptyTitle} desc={emptyDesc} testId={`${scope}-empty`} />;
  return (
    <div className="space-y-3" data-testid={`${scope}-task-list`}>
      {list.map((t, i) => (
        <div key={t.id} className="animate-rise" style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }}>
          <Row t={t} scope={scope} onPick={pick} />
        </div>
      ))}
    </div>
  );
};
